import React from "react";
var __jsx = React.createElement;
import { Fragment, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { formatDateTime, formatDayMonthYear } from "@/utils/format";
import { statusPillClasses } from "../../status-pill";
import { useInfiniteScroll } from "../use-infinite-scroll";
import { SYNC_TABLE_HEADERS } from "./sync-constants";
import { getLifecycleStages, getSyncActionMeta, getSyncStatusIntent, getSyncStatusLabel, getSyncTarget } from "./sync-format";
var TH = "sticky top-0 z-sds-100 bg-sds-base-secondary px-4 py-2.5 text-left text-style-label-caps text-sds-base-secondary";
var TD = "px-4 py-4 align-top text-style-body-small text-sds-base-default";

/** Table of outbound sync submissions with a sticky header and infinite scroll. */
export function SyncSubmissionsTable(_ref) {
  var rows = _ref.rows,
    hasMore = _ref.hasMore,
    isLoadingMore = _ref.isLoadingMore,
    onLoadMore = _ref.onLoadMore;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var scrollRef = useRef(null);
  var sentinelRef = useInfiniteScroll({
    rootRef: scrollRef,
    hasMore: hasMore,
    isLoading: isLoadingMore,
    onLoadMore: onLoadMore
  });
  return /*#__PURE__*/React.createElement("div", {
    ref: scrollRef,
    className: "max-h-[60vh] min-h-[20rem] overflow-auto scrollbar-sds"
  }, /*#__PURE__*/React.createElement("table", {
    className: "w-full min-w-[960px]"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, SYNC_TABLE_HEADERS.map(function (header) {
    return /*#__PURE__*/React.createElement("th", {
      key: header.key,
      scope: "col",
      className: TH
    }, t(header.labelKey, {
      defaultValue: header.fallback
    }));
  }))), /*#__PURE__*/React.createElement("tbody", null, rows.map(function (item) {
    var _item$created_by;
    var action = getSyncActionMeta(item);
    var stages = getLifecycleStages(item);
    var isFailed = item.status.toLowerCase() === "failed";
    var target = getSyncTarget(item);
    return /*#__PURE__*/React.createElement("tr", {
      key: item.id,
      className: "border-t border-sds-base-default"
    }, /*#__PURE__*/React.createElement("td", {
      className: TD
    }, formatDateTime(item.created_at)), /*#__PURE__*/React.createElement("td", {
      className: TD
    }, ((_item$created_by = item.created_by) === null || _item$created_by === void 0 ? void 0 : _item$created_by.trim()) || t("not_available")), /*#__PURE__*/React.createElement("td", {
      className: TD
    }, /*#__PURE__*/React.createElement("span", {
      className: "flex items-center gap-2 text-style-body-small-strong"
    }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: action.icon,
      className: "h-3.5 w-3.5 text-sds-brand-default"
    }), t(action.labelKey, {
      defaultValue: action.fallback
    }))), /*#__PURE__*/React.createElement("td", {
      className: TD
    }, target ? /*#__PURE__*/React.createElement("div", {
      className: "flex flex-col gap-0.5"
    }, target.type || target.start ? /*#__PURE__*/React.createElement("span", {
      className: "text-style-body-small text-sds-base-default"
    }, target.type, target.type && target.start ? " · " : "", target.start ? formatDayMonthYear(target.start) : "") : null, target.id ? /*#__PURE__*/React.createElement("span", {
      className: "text-style-label-base text-sds-base-tertiary"
    }, target.id) : null) : /*#__PURE__*/React.createElement("span", {
      className: "italic text-sds-base-tertiary"
    }, t("not_assigned", {
      defaultValue: "Not assigned"
    }))), /*#__PURE__*/React.createElement("td", {
      className: TD
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex flex-wrap items-center gap-1.5"
    }, stages.map(function (stage, index) {
      var isLastFailed = isFailed && index === stages.length - 1;
      return /*#__PURE__*/React.createElement(Fragment, {
        key: stage
      }, index > 0 ? /*#__PURE__*/React.createElement("span", {
        className: "h-px w-4 bg-sds-success-default"
      }) : null, /*#__PURE__*/React.createElement("span", {
        className: "flex items-center gap-1.5"
      }, /*#__PURE__*/React.createElement("span", {
        className: "h-2 w-2 rounded-full ".concat(isLastFailed ? "bg-sds-urgent-default" : "bg-sds-success-default")
      }), /*#__PURE__*/React.createElement("span", {
        className: "text-style-label-base ".concat(isLastFailed ? "text-sds-urgent-secondary" : "text-sds-success-secondary")
      }, stage)));
    }))), /*#__PURE__*/React.createElement("td", {
      className: TD
    }, /*#__PURE__*/React.createElement("span", {
      className: "inline-flex w-fit items-center gap-2 rounded-sds-full border px-3 py-1 text-style-label-emphasis ".concat(statusPillClasses(getSyncStatusIntent(item.status)))
    }, /*#__PURE__*/React.createElement("span", {
      className: "h-1.5 w-1.5 rounded-full bg-current"
    }), getSyncStatusLabel(item.status))));
  }))), /*#__PURE__*/React.createElement("div", {
    ref: sentinelRef
  }), isLoadingMore ? /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-center gap-2 py-4 text-style-label-base text-sds-base-tertiary"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faSpinner,
    className: "h-3.5 w-3.5 animate-spin"
  }), t("loading_more", {
    defaultValue: "Loading more…"
  })) : null);
}